import React from "react";
import { useLocation } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { isAdsenseAutoAdsPath } from "@/lib/adsensePolicy";

const CONSENT_KEY = "marstick_cookie_consent_v2";
const CONTAINER_CLASS = "publisher-ad-network-placement";

function getSection(pathname) {
  const path = pathname.toLowerCase();
  if (path === "/" || /^\/[a-z]{2}\/?$/.test(path)) return "homepage";
  if (path.includes("learning") || path.includes("wire")) return "articles";
  if (path.includes("marina")) return "marinas";
  if (path.includes("weather")) return "weather";
  if (path.includes("calculator") || path.includes("tool")) return "calculators";
  if (path.includes("safety")) return "safety";
  if (path.includes("glossary")) return "glossary";
  if (path.includes("regulation")) return "regulations";
  if (path.includes("community")) return "community";
  if (path.includes("premium") || path.includes("billing")) return "premium";
  return "homepage";
}

function hasAdvertisingConsent() {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) return false;
    if (saved === "accepted") return true;
    if (saved === "rejected") return false;
    return JSON.parse(saved)?.advertising === true;
  } catch { return false; }
}

function executeHtml(html, target) {
  if (!html?.trim() || !target) return;
  const template = document.createElement("template");
  template.innerHTML = html;
  Array.from(template.content.childNodes).forEach((node) => {
    if (node.nodeName.toLowerCase() !== "script") return target.appendChild(node.cloneNode(true));
    const sourceScript = node;
    if (sourceScript.src && document.querySelector(`script[src="${sourceScript.src}"]`)) return;
    const script = document.createElement("script");
    Array.from(sourceScript.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
    script.text = sourceScript.textContent || "";
    target.appendChild(script);
  });
}

function clearPlacements() {
  document.querySelectorAll(`.${CONTAINER_CLASS}`).forEach((node) => node.remove());
}

function mountPlacement(item) {
  const isMobile = window.matchMedia?.("(max-width: 767px)").matches;
  const position = isMobile ? item.mobile_position : item.desktop_position;
  const container = document.createElement("section");
  container.className = `${CONTAINER_CLASS} maritime-container my-8`;
  container.setAttribute("aria-label", `${item.label || item.network} sponsored ads`);
  executeHtml(item.placement_code, container);
  if (!container.childNodes.length) return;

  const main = document.getElementById("main-content") || document.querySelector("main");
  const footer = document.querySelector("footer");
  if (position === "top" && main) main.prepend(container);
  else if (position === "footer" && footer?.parentNode) footer.parentNode.insertBefore(container, footer);
  else if (main) main.appendChild(container);
  else document.body.appendChild(container);
}

export default function PublisherAdNetworkLoader() {
  const location = useLocation();
  const [consented, setConsented] = React.useState(() => typeof window !== "undefined" && hasAdvertisingConsent());
  const allowedPath = isAdsenseAutoAdsPath(location.pathname, location.search);

  React.useEffect(() => {
    const onConsent = () => setConsented(hasAdvertisingConsent());
    window.addEventListener("marstick:consent-updated", onConsent);
    window.addEventListener("storage", onConsent);
    return () => { window.removeEventListener("marstick:consent-updated", onConsent); window.removeEventListener("storage", onConsent); };
  }, []);

  React.useEffect(() => {
    if (!allowedPath || !consented) { clearPlacements(); return; }
    let mounted = true;
    const section = getSection(location.pathname);
    base44.entities.AdNetworkSetting.list("network").then((records = []) => {
      if (!mounted) return;
      clearPlacements();
      records.filter((item) => item.is_enabled && item.site_sections?.includes(section)).forEach((item) => {
        executeHtml(item.head_code, document.head);
        mountPlacement(item);
      });
    });
    return () => { mounted = false; clearPlacements(); };
  }, [allowedPath, consented, location.pathname, location.search]);

  return null;
}